/**
 * service-mapper.js
 *
 * Responsabilidad única: traducir datos entre la UI y el Backend para servicios.
 *
 * - mapServiceToApi     : FormData (UI strings) → payload limpio para POST/PUT
 * - mapServiceForTable  : Objeto crudo del Backend → objeto ordenado para la Table
 * - mapServicesListForTable : Lista de servicios crudos → lista mapeada para la Table
 */

/**
 * Mapea el formData del ServiceForm al payload exacto que espera el Backend.
 * Aplica casteo estricto para evitar strings donde el Backend espera numbers/booleans.
 *
 * @param {Object} formData - Estado del formulario de la UI.
 * @param {'create'|'edit'} mode - Modo del formulario.
 * @returns {Object} Payload listo para enviar al API.
 */
export const mapServiceToApi = (formData, mode = 'create') => {
    const payload = {
        name: String(formData.name ?? '').trim(),
        description: String(formData.description ?? '').trim() || null,
        value: Number(formData.value),
        unit: String(formData.unit ?? '70'),
        taxes_id: formData.taxes_id ? Number(formData.taxes_id) : null,
    };

    // SKU solo se envía en creación (en edición el backend lo ignora / está disabled)
    if (mode === 'create') {
        payload.sku = String(formData.sku ?? '').trim().toUpperCase();
    }

    // is_active solo se envía en edición
    if (mode === 'edit') {
        payload.is_active = Boolean(formData.is_active);
    }

    return payload;
};

/**
 * Mapea un servicio crudo del Backend al formato y orden de columnas que
 * espera el componente Table de la UI.
 *
 * Orden de columnas: sku | Nombre | Descripción | Valor | Unidad | Impuesto | Activo
 *
 * @param {Object} service - Servicio crudo del backend.
 * @returns {Object} Servicio mapeado con las llaves en el orden correcto.
 */
export const mapServiceForTable = (service) => {
    if (!service) return service;

    const mapped = {
        // Columnas visibles (en orden de aparición en la tabla)
        sku: service.sku ?? '',
        name: service.name ?? '',
        description: service.description ?? '',
        value: Number(service.value ?? 0),
        unit: String(service.unit ?? ''),
        taxes_id: service.taxes_id ? Number(service.taxes_id) : null,
        is_active: service.is_active !== undefined ? Boolean(service.is_active) : true,
        is_service: true,
    };

    // Copiar el resto de propiedades del backend (id, users_id, created_at, updated_at)
    // sin alterar el orden de columnas ya definido.
    Object.keys(service).forEach((key) => {
        if (!(key in mapped)) {
            mapped[key] = service[key];
        }
    });

    return mapped;
};

/**
 * Mapea una lista de servicios usando el mapeador individual.
 *
 * @param {Array} services - Lista de servicios crudos del backend.
 * @returns {Array} Lista de servicios mapeados para la Table.
 */
export const mapServicesListForTable = (services) => {
    if (!Array.isArray(services)) return [];
    return services.map(mapServiceForTable);
};
